import React, { useState } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { useNavigate } from 'react-router-dom';
import gql from 'graphql-tag';

const PUBLISH_JOB_MUTATION = gql`
  mutation publishJob($job: JobInput!) {
    publishJob(job: $job) {
      id
      puesto
      rubro
      conocimientosDuros
      habilidadesBlandas
      additionalFields
    }
  }
`;

const JobItem = ({ job }) => {
  const [currentJob, setCurrentJob] = useState(job);
  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState({
    puesto: currentJob.puesto,
    rubro: currentJob.rubro,
    conocimientosDuros: Array.isArray(currentJob.conocimientosDuros)
      ? currentJob.conocimientosDuros.join(", ")
      : currentJob.conocimientosDuros || "",
    habilidadesBlandas: Array.isArray(currentJob.habilidadesBlandas)
      ? currentJob.habilidadesBlandas.join(", ")
      : currentJob.habilidadesBlandas || "",
    additionalFields: currentJob.additionalFields
      ? { ...currentJob.additionalFields }
      : {}
  });
  const [newFields, setNewFields] = useState([]);
  const navigate = useNavigate();

  // Mutación para publicar el job en la colección JobsActive
  const [publishJob, { loading: publishLoading, error: publishError }] = useMutation(
    PUBLISH_JOB_MUTATION,
    {
      onCompleted: (data) => {
        // Una vez publicado, se redirige a la página de detalle usando el id del job publicado
        navigate(`/job/${data.publishJob.id}`);
      }
    }
  );

  const handleRemoveAdditionalField = (key) => {
    setEditedJob(prev => {
      const updated = { ...prev.additionalFields };
      delete updated[key];
      return { ...prev, additionalFields: updated };
    });
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = () => {
    setCurrentJob({
      ...currentJob,
      puesto: editedJob.puesto,
      rubro: editedJob.rubro,
      conocimientosDuros: editedJob.conocimientosDuros,
      habilidadesBlandas: editedJob.habilidadesBlandas,
      additionalFields: editedJob.additionalFields
    });
    setIsEditing(false);
  };

  const handleAddNewField = () => {
    setNewFields([...newFields, { label: '', value: '' }]);
  };

  const handleNewFieldLabelChange = (index, value) => {
    const updatedFields = [...newFields];
    updatedFields[index].label = value;
    setNewFields(updatedFields);
  };

  const handleNewFieldValueChange = (index, value) => {
    const updatedFields = [...newFields];
    updatedFields[index].value = value;
    setNewFields(updatedFields);
  };

  const handleDefineNewField = (index) => {
    const field = newFields[index];
    if (field.label.trim() === '') {
      alert("El nombre del campo no puede estar vacío");
      return;
    }
    setEditedJob(prev => ({
      ...prev,
      additionalFields: {
        ...prev.additionalFields,
        [field.label]: field.value
      }
    }));
    setNewFields(newFields.filter((_, i) => i !== index));
  };

  const handleRemoveNewField = (index) => {
    setNewFields(newFields.filter((_, i) => i !== index));
  };

  // Función para publicar el trabajo (usa currentJob que equivale a editedJob guardado)
  const handlePublish = () => {
    publishJob({
      variables: {
        job: {
          puesto: currentJob.puesto,
          rubro: currentJob.rubro,
          conocimientosDuros: currentJob.conocimientosDuros,
          habilidadesBlandas: currentJob.habilidadesBlandas,
          additionalFields: currentJob.additionalFields
        }
      }
    });
  };

  if (isEditing) {
    return (
      <div className="jobItem">
        <div className="defaultFields">
          <div className="fieldGroup">
            <label className="label">Puesto:</label>
            <input type="text" value={editedJob.puesto} readOnly className="readOnlyInput" />
          </div>
          <div className="fieldGroup">
            <label className="label">Rubro:</label>
            <input type="text" value={editedJob.rubro} readOnly className="readOnlyInput" />
          </div>
          <div className="fieldGroup">
            <label className="label">Conocimientos Duros:</label>
            <input type="text" value={editedJob.conocimientosDuros} readOnly className="readOnlyInput" />
          </div>
          <div className="fieldGroup">
            <label className="label">Habilidades Blandas:</label>
            <input type="text" value={editedJob.habilidadesBlandas} readOnly className="readOnlyInput" />
          </div>
          {Object.keys(editedJob.additionalFields).length > 0 && (
            <div>
              <strong>Información adicional:</strong>
              {Object.keys(editedJob.additionalFields).map((key, index) => (
                <div key={index} className="fieldGroup">
                  <label className="label">{key}:</label>
                  <input type="text" value={editedJob.additionalFields[key]} readOnly className="readOnlyInput" />
                  <button onClick={() => handleRemoveAdditionalField(key)} className="removeButton">X</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <hr className="separator" />
        <div className="newFieldsSection">
          {newFields.map((field, index) => (
            <div key={index} className="newFieldGroup">
              <input 
                type="text" 
                placeholder="Título de la información" 
                value={field.label} 
                onChange={(e) => handleNewFieldLabelChange(index, e.target.value)} 
                className="editableInput"
              />
              <input 
                type="text" 
                placeholder="Aquí escriba la información adicional" 
                value={field.value} 
                onChange={(e) => handleNewFieldValueChange(index, e.target.value)} 
                className="editableInput"
              />
              <button onClick={() => handleDefineNewField(index)} className="defineButton">
                Definir
              </button>
              <button onClick={() => handleRemoveNewField(index)} className="removeButton">
                X
              </button>
            </div>
          ))}
          <button onClick={handleAddNewField} className="addFieldButton">
            Agregar información
          </button>
        </div>
        <button onClick={handleSaveClick} className="saveButton">Guardar</button>
      </div>
    );
  }

  return (
    <div className="jobItem">
      <h4 className="jobTitle">
        {currentJob.puesto} - {currentJob.rubro}
      </h4>
      <p>
        <strong>Conocimientos Duros:</strong>{" "}
        {(Array.isArray(currentJob.conocimientosDuros)
          ? currentJob.conocimientosDuros.join(", ")
          : currentJob.conocimientosDuros || "").toString()}
      </p>
      <p>
        <strong>Habilidades Blandas:</strong>{" "}
        {(Array.isArray(currentJob.habilidadesBlandas)
          ? currentJob.habilidadesBlandas.join(", ")
          : currentJob.habilidadesBlandas || "").toString()}
      </p>
      {currentJob.additionalFields && Object.keys(currentJob.additionalFields).length > 0 && (
        <div>
          <strong>Información adicional:</strong>
          {Object.keys(currentJob.additionalFields).map((key, index) => (
            <div key={index} className="fieldGroup">
              <p className="fieldText">
                <strong>{key}:</strong> {currentJob.additionalFields[key]}
              </p>
            </div>
          ))}
        </div>
      )}
      <div className="buttonGroup">
        <button onClick={handleEditClick}>Editar</button>
        <button onClick={handlePublish}>Publicar</button>
      </div>
      {publishLoading && <p>Publicando...</p>}
      {publishError && <p>Error: {publishError.message}</p>}
    </div>
  );
};

export default JobItem;
